<script setup>
import { useModalogStore } from "@/stores/modalog.js";
import { useModalogEscape } from "@/composables/useModalogEscape.js";

/**
 * Shared chrome for new-brand modalogs (GH-848): navy overlay, white card,
 * gradient header with title + close, and a body slot. Modalogs using this
 * shell must keep a stable root and gate the shell on hasModalog themselves
 * (see ManageProfileThemeModalog for why a conditional root misbehaves).
 */
defineProps({
    title: {
        type: String,
        required: true,
    },
    // Card width cap; the onboarding tour (GH-1056) runs wider than the
    // default form modalogs.
    maxWidthClass: {
        type: String,
        default: "max-w-[470px]",
    },
});

const modalogStore = useModalogStore();

useModalogEscape();
</script>

<template>
    <div
        class="brand fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-navy/60 p-4 font-brand text-brand-navy sm:p-8"
        @click.self="modalogStore.closeModalog()"
    >
        <div
            class="relative my-auto w-full overflow-hidden rounded-[5px] bg-white shadow-brand-hero"
            :class="maxWidthClass"
        >
            <header
                class="flex h-20 items-center justify-between px-9"
                :style="{
                    backgroundImage:
                        'linear-gradient(-76.92deg, #f21439 17.48%, #ffb545 116.06%)',
                }"
            >
                <h2 class="text-[25px] font-bold text-white">{{ title }}</h2>
                <button
                    type="button"
                    class="grid size-8 place-items-center text-white transition hover:opacity-75"
                    aria-label="Close"
                    @click="modalogStore.closeModalog()"
                >
                    <svg
                        viewBox="0 0 19 19"
                        class="size-[19px]"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        aria-hidden="true"
                    >
                        <path d="M2 2l15 15M17 2L2 17" />
                    </svg>
                </button>
            </header>
            <!-- The [&_p]/[&_li] variants out-specify the legacy `p { line-height: 1 }`
                 reset in global/_base.scss, which otherwise beats inherited leading. -->
            <div
                class="px-9 pb-9 pt-7 [&_li]:leading-relaxed [&_p]:leading-relaxed"
            >
                <slot />
            </div>
        </div>
    </div>
</template>
